import { PAGE_API_QUERY_KEY_HEAD } from '@Core/clients/pageApiQueryKeys';
import { usePageRuntime } from '@Pages/pageRuntime/pageRuntimeContextValue';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  ApiError,
  SdkApiHandle,
  SdkBulkNotificationRun,
  SdkSendBulkNotificationsHandle,
  SdkSendBulkNotificationsInput
} from '@Pages/pageRuntime/sdk/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect } from 'react';

import { useApi } from './useApi';

export const BULK_NOTIFICATION_RUNS_URL = '/api/email-messages/bulk-notification-runs';

function useBulkNotificationQueryKey() {
  const sdk = usePageRuntime()?.Sdk;

  return [
    'bulk-notification-runs',
    sdk?.workspace?.id ?? null,
    sdk?.identity?.workspace_id ?? null,
    sdk?.identity?.resource_id ?? null,
    sdk?.identity?.id ?? null
  ] as const;
}

export function useSendBulkNotifications(): SdkSendBulkNotificationsHandle {
  const queryClient = useQueryClient();
  const queryKey = useBulkNotificationQueryKey();
  const mutation = useMutation<SdkBulkNotificationRun, ApiError, SdkSendBulkNotificationsInput>({
    retry: false,
    mutationFn: async (input) => {
      try {
        const run = (await axios.post<SdkBulkNotificationRun>(BULK_NOTIFICATION_RUNS_URL, input))
          .data;
        queryClient.setQueryData([PAGE_API_QUERY_KEY_HEAD, ...queryKey, run.id], run);
        return run;
      } catch (error) {
        throw toApiError(error);
      }
    }
  });

  return {
    send: (input) => mutation.mutateAsync(input).catch(() => null),
    data: mutation.data,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: () => mutation.reset()
  };
}

export function useBulkNotificationRun(
  runId: string | null | undefined
): SdkApiHandle<SdkBulkNotificationRun> {
  const queryClient = useQueryClient();
  const queryKey = useBulkNotificationQueryKey();
  const query = useApi<SdkBulkNotificationRun>(
    [...queryKey, runId],
    (api) => api.get(`${BULK_NOTIFICATION_RUNS_URL}/${encodeURIComponent(runId ?? '')}`),
    {
      enabled: !!runId,
      pollInterval: (run) =>
        !run || run.status === 'queued' || run.status === 'processing' ? 2000 : false
    }
  );
  const queuedCount = query.data?.queued_count;
  useEffect(() => {
    if (queuedCount !== undefined && queuedCount > 0) {
      void queryClient.invalidateQueries({ queryKey: ['email-messages', 'search'] });
    }
  }, [queryClient, runId, queuedCount]);

  return query;
}
